import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { SeasonScheduleState } from '../models/game';
import { User } from '../models/user';
import { PlaybookPlay, PlaybookPlayType, PlaybookStructureState } from '../models/playbook';
import { AiScoutHistoryItem } from '../models/ai-scout';
import { TutorialType } from '../models/usersettings';
import moment from 'moment';

@Injectable()
export class LocalStorageService {

  constructor() { }

  private userUpdate = new Subject<any>();
  private themeUpdate = new Subject<Theme>();

  listenForUserUpdate(): Observable<User> {
    return this.userUpdate.asObservable();
  }

  emitUserUpdate(user: User) {
    this.userUpdate.next(user);
  }

  setToken(token: string) {
    localStorage.setItem('token', token);
  }

  getToken(): string {
    return localStorage.getItem('token');
  }

  removeToken() {
    localStorage.removeItem('token');
  }

  setUser(user: User) {
    localStorage.setItem('user', JSON.stringify(user));
    this.emitUserUpdate(user);
  }

  getUser(): User {
    const userStr = localStorage.getItem('user');
    if (userStr) return JSON.parse(userStr);
    return null;
  }

  removeUser() {
    localStorage.removeItem('user');
    this.emitUserUpdate(null);
  }

  hasPasskeyForDevice(userid: string): boolean {
    return !!userid && localStorage.getItem(`qwikcut_passkey_device:${userid}`) === 'true';
  }

  markPasskeyForDevice(userid: string) {
    if (userid) localStorage.setItem(`qwikcut_passkey_device:${userid}`, 'true');
  }

  clearPasskeyForDevice(userid: string) {
    if (userid) localStorage.removeItem(`qwikcut_passkey_device:${userid}`);
  }

  getClipViewQueue<T>(userid: string, teamid: string): T[] {
    if (!userid || !teamid) return [];
    try {
      return JSON.parse(localStorage.getItem(`clipViewQueue:${userid}:${teamid}`) || '[]') || [];
    }
    catch (err) {
      console.log('Unable to read queued clip views', err);
      return [];
    }
  }

  setClipViewQueue<T>(userid: string, teamid: string, queue: T[]) {
    if (!userid || !teamid) return;
    localStorage.setItem(`clipViewQueue:${userid}:${teamid}`, JSON.stringify(queue || []));
  }

  removeClipViewQueue(userid: string, teamid: string) {
    if (userid && teamid) localStorage.removeItem(`clipViewQueue:${userid}:${teamid}`);
  }

  getPlaybookViewTimeQueue<T>(userid: string): T[] {
    if (!userid) return [];
    try {
      return JSON.parse(localStorage.getItem(`playbookViewTimeQueue:${userid}`) || '[]') || [];
    }
    catch (err) {
      console.log('Unable to read queued playbook view time', err);
      return [];
    }
  }

  setPlaybookViewTimeQueue<T>(userid: string, queue: T[]) {
    if (!userid) return;
    const key = `playbookViewTimeQueue:${userid}`;
    if (queue?.length) localStorage.setItem(key, JSON.stringify(queue));
    else localStorage.removeItem(key);
  }

  setTheme(theme: Theme) {
    localStorage.setItem('theme', theme);
    this.emitThemeUpdate(theme);
  }

  getTheme(): Theme {
    return <Theme>localStorage.getItem('theme') || 'dark';
  }

  setThemeToggleSeen() {
    localStorage.setItem('themeToggleSeen', 'true');
  }

  getThemeToggleSeen(): boolean {
    return localStorage.getItem('themeToggleSeen') === 'true';
  }

  /** The Organization Management help dialog has been shown once on this browser. */
  setOrganizationManagementHelpSeen() {
    localStorage.setItem('organizationManagementHelpSeen', 'true');
  }

  getOrganizationManagementHelpSeen(): boolean {
    return localStorage.getItem('organizationManagementHelpSeen') === 'true';
  }

  setAutoHighlightIntroSeen() {
    localStorage.setItem('autoHighlightsIntroSeen', 'true');
  }

  getAutoHighlightIntroSeen(): boolean {
    return localStorage.getItem('autoHighlightsIntroSeen') === 'true';
  }

  hasFormationPerspectiveHelpBeenSeen(userid: string, teamid: string): boolean {
    return !!userid && !!teamid && localStorage.getItem(`formationPerspectiveHelpSeen:${userid}:${teamid}`) === 'true';
  }

  markFormationPerspectiveHelpSeen(userid: string, teamid: string) {
    if (userid && teamid) localStorage.setItem(`formationPerspectiveHelpSeen:${userid}:${teamid}`, 'true');
  }

  setAiScoutInfoBannerDismissed(dismissed: boolean) {
    localStorage.setItem('aiScoutInfoBannerDismissed', dismissed ? 'true' : 'false');
  }

  getAiScoutInfoBannerDismissed(): boolean {
    return localStorage.getItem('aiScoutInfoBannerDismissed') === 'true';
  }

  hasAiScoutTutorialBeenOffered(userid: string, tutorial: TutorialType): boolean {
    return !!userid && !!tutorial && localStorage.getItem(`aiScoutTutorialOffered:${userid}:${tutorial}`) === 'true';
  }

  markAiScoutTutorialOffered(userid: string, tutorial: TutorialType) {
    if (userid && tutorial) localStorage.setItem(`aiScoutTutorialOffered:${userid}:${tutorial}`, 'true');
  }

  getAiScoutHistory(userid: string, teamid: string): AiScoutHistoryItem[] {
    const key = this.getAiScoutHistoryKey(userid, teamid);
    if (!key) return [];
    try {
      const history = JSON.parse(localStorage.getItem(key) || '[]') || [];
      return _.orderBy(history, item => new Date(item?.createdAt || 0).getTime(), 'desc').slice(0, 20);
    }
    catch (err) {
      console.log(err);
      return [];
    }
  }

  setAiScoutHistory(userid: string, teamid: string, history: AiScoutHistoryItem[]) {
    const key = this.getAiScoutHistoryKey(userid, teamid);
    if (!key) return;
    const cleanHistory = _.orderBy(history || [], item => new Date(item?.createdAt || 0).getTime(), 'desc').slice(0, 20);
    localStorage.setItem(key, JSON.stringify(cleanHistory));
  }

  addAiScoutHistoryItem(userid: string, teamid: string, item: AiScoutHistoryItem): AiScoutHistoryItem[] {
    const history = this.getAiScoutHistory(userid, teamid);
    const nextHistory = [
      item,
      ..._.filter(history, historyItem => historyItem?.id !== item?.id && this.getAiScoutHistorySignature(historyItem) !== this.getAiScoutHistorySignature(item))
    ];
    this.setAiScoutHistory(userid, teamid, nextHistory);
    return this.getAiScoutHistory(userid, teamid);
  }

  removeAiScoutHistoryItem(userid: string, teamid: string, id: string): AiScoutHistoryItem[] {
    const nextHistory = _.filter(this.getAiScoutHistory(userid, teamid), item => item?.id !== id);
    this.setAiScoutHistory(userid, teamid, nextHistory);
    return nextHistory;
  }

  clearAiScoutHistory(userid: string, teamid: string) {
    const key = this.getAiScoutHistoryKey(userid, teamid);
    if (key) localStorage.removeItem(key);
  }

  private getAiScoutHistoryKey(userid: string, teamid: string): string {
    if (!userid || !teamid) return '';
    return `aiScoutReportHistory:${userid}:${teamid}`;
  }

  private getAiScoutHistorySignature(item: AiScoutHistoryItem): string {
    return `${_.trim(item?.prompt || '')}|${item?.scoutType || ''}|${_.join(_.sortBy(_.map(item?.selectedScoutTeams || [], selection => selection?.gameid)), ',')}`;
  }

  listenForThemeUpdate(): Observable<Theme> {
    return this.themeUpdate.asObservable();
  }

  private emitThemeUpdate(theme: Theme) {
    this.themeUpdate.next(theme);
  }

  setScheduleState(teamid: string, seasonStates: {[seasonid: number]: SeasonScheduleState}) {
    let scheduleState: {[teamid: string]: {[seasonid: number]: SeasonScheduleState}} = JSON.parse(localStorage.getItem('scheduleState'));
    if (!scheduleState) scheduleState = {};
    scheduleState[teamid] = seasonStates;
    localStorage.setItem('scheduleState', JSON.stringify(scheduleState));
  }

  getScheduleState(teamid: string): {[seasonid: number]: SeasonScheduleState} {
    const scheduleState: {[teamid: string]: {[seasonid: number]: SeasonScheduleState}} = JSON.parse(localStorage.getItem('scheduleState'));
    if (scheduleState && scheduleState[teamid]) return scheduleState[teamid];
    return {};
  }

  setPlaybookState(teamid: string, playbookStates: {[type: string]: PlaybookStructureState}) {
    let playbookState: {[teamid: string]: {[type: string]: PlaybookStructureState}} = JSON.parse(localStorage.getItem('playbookState'));
    playbookState ??= {};
    playbookState[teamid] = playbookStates;
    localStorage.setItem('playbookState', JSON.stringify(playbookState));
  }

  getPlaybookState(teamid: string): {[type: string]: PlaybookStructureState} {
    const playbookState: {[teamid: string]: {[type: string]: PlaybookStructureState}} = JSON.parse(localStorage.getItem('playbookState'));
    if (playbookState && playbookState[teamid]) return playbookState[teamid];
    return {};
  }

  setLastOpenedPlaybookFolder(teamid: string, type: PlaybookPlayType, folderid: string, installFolder: boolean) {
    let openedPlaybookFolderState: {[teamid: string]: {type: PlaybookPlayType, folderid: string, installFolder: boolean}} = JSON.parse(localStorage.getItem('playbookLastOpenedFolder'));
    openedPlaybookFolderState ??= {};
    openedPlaybookFolderState[teamid] = {type, folderid, installFolder};
    localStorage.setItem('playbookLastOpenedFolder', JSON.stringify(openedPlaybookFolderState));
  }

  getLastOpenedPlaybookFolder(teamid: string): {type?: PlaybookPlayType, folderid?: string, installFolder?: boolean} {
    const openedPlaybookFolderState: {[teamid: string]: {type: PlaybookPlayType, folderid: string, installFolder: boolean}} = JSON.parse(localStorage.getItem('playbookLastOpenedFolder'));
    if (openedPlaybookFolderState && openedPlaybookFolderState[teamid]) return openedPlaybookFolderState[teamid];
    return {};
  }

  setRecentlyUsedPlaybookFormations(type: PlaybookPlayType, formationPlays: PlaybookPlay[]) {
    localStorage.setItem(`playbookRecentFormations-${type}`, JSON.stringify(formationPlays));
  }

  getRecentlyUsedPlaybookFormations(type: PlaybookPlayType): PlaybookPlay[] {
    return JSON.parse(localStorage.getItem(`playbookRecentFormations-${type}`) ?? '[]') ?? [];
  }

  setPresentationState(userid: string, state: PlaybookStructureState) {
    if (!userid) return;
    let presentationState: {[userid: string]: PlaybookStructureState} = JSON.parse(localStorage.getItem('presentationState'));
    presentationState ??= {};
    presentationState[userid] = state;
    localStorage.setItem('presentationState', JSON.stringify(presentationState));
  }

  getPresentationState(userid: string): PlaybookStructureState {
    const presentationState: {[userid: string]: PlaybookStructureState} = JSON.parse(localStorage.getItem('presentationState'));
    if (presentationState && presentationState[userid]) return presentationState[userid];
    return {expandedNodeIds: [], lastScrollPos: 0};
  }

  setLastOpenedPresentationFolder(userid: string, folderid: string) {
    if (!userid) return;
    let openedFolderState: {[userid: string]: string} = JSON.parse(localStorage.getItem('presentationLastOpenedFolder'));
    openedFolderState ??= {};
    openedFolderState[userid] = folderid;
    localStorage.setItem('presentationLastOpenedFolder', JSON.stringify(openedFolderState));
  }

  getLastOpenedPresentationFolder(userid: string): string {
    const openedPlaybookFolderState: {[userid: string]: string} = JSON.parse(localStorage.getItem('presentationLastOpenedFolder'));
    if (openedPlaybookFolderState && openedPlaybookFolderState[userid]) return openedPlaybookFolderState[userid];
    return null;
  }

  getHofAdDismissDate(): Date {
    const dateStr = localStorage.getItem('hofAdDismissDate');
    if (dateStr) return new Date(dateStr);
    return null;
  }

  setHofAdDismissDate(date: Date) {
    localStorage.setItem('hofAdDismissDate', date.toISOString());
  }

  getHofAdDismissed(): boolean {
    const date = this.getHofAdDismissDate();
    if (!date) return false;
    const now = new Date();
    const diffInDays = moment(now).diff(moment(date), 'days');
    return diffInDays < 7; // Dismiss for 7 days
  }

}

export type Theme = 'dark' | 'light';
